import { useState, useRef, useEffect, useCallback } from 'react';
import { useModalStore } from '../../stores/modal';

interface CapturedPhoto {
  blob: Blob;
  previewUrl: string;
}

interface CameraCaptureModalProps {
  onConfirm: (files: File[]) => void;
}

export default function CameraCaptureModal({ onConfirm }: CameraCaptureModalProps) {
  const { cameraCapture, closeCameraCapture } = useModalStore();
  const isOpen = cameraCapture;

  const [phase, setPhase] = useState<'capture' | 'select'>('capture');
  const [capturedPhotos, setCapturedPhotos] = useState<CapturedPhoto[]>([]);
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [error, setError] = useState<string | null>(null);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const thumbnailScrollRef = useRef<HTMLDivElement>(null);

  // 카메라 스트림 시작
  const startCamera = useCallback(async (facing: 'environment' | 'user') => {
    try {
      // 기존 스트림 정리
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        setError('이 브라우저에서는 카메라 촬영을 지원하지 않습니다.');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facing,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setError(null);
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('카메라 접근 권한이 필요합니다. 브라우저 설정에서 카메라 권한을 허용해주세요.');
      } else if (err.name === 'NotFoundError') {
        setError('카메라를 찾을 수 없습니다.');
      } else {
        setError('카메라를 시작할 수 없습니다.');
      }
    }
  }, []);

  // 카메라 장치 개수 확인
  const checkCameraDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      setHasMultipleCameras(videoDevices.length > 1);
    } catch {
      setHasMultipleCameras(false);
    }
  }, []);

  // 모달 열릴 때 카메라 시작
  useEffect(() => {
    if (isOpen) {
      setPhase('capture');
      setCapturedPhotos([]);
      setSelectedIndexes(new Set());
      setError(null);
      setFacingMode('environment');
      checkCameraDevices();
      startCamera('environment');
    }

    return () => {
      // 모달 닫힐 때 스트림 정리
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [isOpen, startCamera, checkCameraDevices]);

  // capturedPhotos 변경 시 previewUrl 정리 (unmount)
  useEffect(() => {
    return () => {
      capturedPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
  }, []);

  // 카메라 전환
  const handleSwitchCamera = useCallback(() => {
    const newFacing = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacing);
    startCamera(newFacing);
  }, [facingMode, startCamera]);

  // 셔터 사운드
  const playShutterSound = useCallback(() => {
    try {
      const audio = new Audio('/sounds/mixkit-camera-shutter-click-1133.wav');
      audio.volume = 0.5;
      audio.play();
    } catch {
      // 오디오 미지원 시 무시
    }
  }, []);

  // 사진 촬영
  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    playShutterSound();

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const previewUrl = URL.createObjectURL(blob);
        setCapturedPhotos((prev) => [...prev, { blob, previewUrl }]);

        // 새 썸네일로 스크롤
        setTimeout(() => {
          if (thumbnailScrollRef.current) {
            thumbnailScrollRef.current.scrollLeft = thumbnailScrollRef.current.scrollWidth;
          }
        }, 50);
      },
      'image/jpeg',
      0.9
    );
  }, [playShutterSound]);

  // 촬영된 사진 삭제
  const handleRemovePhoto = useCallback((index: number) => {
    setCapturedPhotos((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // 촬영 완료 → 선택 모드
  const handleFinishCapture = useCallback(() => {
    if (capturedPhotos.length === 0) return;

    // 카메라 스트림 중지
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    // 모든 사진 기본 선택
    setSelectedIndexes(new Set(capturedPhotos.map((_, i) => i)));
    setPhase('select');
  }, [capturedPhotos]);

  // 선택 토글
  const handleToggleSelect = useCallback((index: number) => {
    setSelectedIndexes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  // 전체 선택/해제
  const handleToggleAll = useCallback(() => {
    if (selectedIndexes.size === capturedPhotos.length) {
      setSelectedIndexes(new Set());
    } else {
      setSelectedIndexes(new Set(capturedPhotos.map((_, i) => i)));
    }
  }, [selectedIndexes.size, capturedPhotos.length]);

  // 선택 모드에서 돌아가기 (카메라 재시작)
  const handleBackToCapture = useCallback(() => {
    setPhase('capture');
    startCamera(facingMode);
  }, [facingMode, startCamera]);

  // 선택한 사진 추가
  const handleConfirm = useCallback(() => {
    const files = Array.from(selectedIndexes)
      .sort((a, b) => a - b)
      .map((index) => {
        const photo = capturedPhotos[index];
        const timestamp = Date.now();
        return new File([photo.blob], `camera_${timestamp}_${index}.jpg`, {
          type: 'image/jpeg',
        });
      });

    if (files.length > 0) {
      onConfirm(files);
    }

    // 정리
    capturedPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setCapturedPhotos([]);
    setSelectedIndexes(new Set());
    closeCameraCapture();
  }, [selectedIndexes, capturedPhotos, onConfirm, closeCameraCapture]);

  // 모달 닫기
  const handleClose = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    capturedPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setCapturedPhotos([]);
    setSelectedIndexes(new Set());
    closeCameraCapture();
  }, [capturedPhotos, closeCameraCapture]);

  if (!isOpen) return null;

  const selectedCount = selectedIndexes.size;

  return (
    <div className="fixed inset-0 bg-black flex flex-col" style={{ zIndex: 9999 }}>
      {/* 숨겨진 캔버스 (캡처용) */}
      <canvas ref={canvasRef} className="hidden" />

      {phase === 'capture' ? (
        <>
          {/* 상단 바 */}
          <div className="flex items-center justify-between px-4 py-3 bg-black/80 z-10">
            <button
              type="button"
              onClick={handleClose}
              className="text-white p-2"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <span className="text-white text-sm">
              {capturedPhotos.length > 0 && `${capturedPhotos.length}장 촬영`}
            </span>

            {hasMultipleCameras && (
              <button
                type="button"
                onClick={handleSwitchCamera}
                className="text-white p-2"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
            {!hasMultipleCameras && <div className="w-10" />}
          </div>

          {/* 카메라 프리뷰 또는 에러 */}
          <div className="flex-1 relative bg-black flex items-center justify-center overflow-hidden">
            {error ? (
              <div className="text-center px-8">
                <div className="text-red-400 text-lg mb-4">
                  <svg className="w-16 h-16 mx-auto mb-3 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <p className="text-white text-sm">{error}</p>
              </div>
            ) : (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            )}
          </div>

          {/* 하단 촬영된 사진 썸네일 (가로 스크롤) */}
          {capturedPhotos.length > 0 && (
            <div
              ref={thumbnailScrollRef}
              className="flex gap-2 px-4 py-2 bg-black/80 overflow-x-auto"
            >
              {capturedPhotos.map((photo, idx) => (
                <div key={idx} className="relative flex-shrink-0 w-14 h-14">
                  <img
                    src={photo.previewUrl}
                    alt={`촬영 ${idx + 1}`}
                    className="w-full h-full object-cover rounded-card-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(idx)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                  >
                    x
                  </button>
                  <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-center text-[10px]">
                    {idx + 1}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* 하단 컨트롤 */}
          <div className="relative flex items-center justify-center px-4 py-4 bg-black/80" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            {/* 촬영 버튼 (중앙 고정) */}
            <button
              type="button"
              onClick={handleCapture}
              disabled={!!error}
              className="w-16 h-16 rounded-full border-4 border-white bg-white/20 active:bg-white/40 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <div className="w-12 h-12 rounded-full bg-white" />
            </button>

            {/* 완료 버튼 (오른쪽) */}
            {capturedPhotos.length > 0 && (
              <button
                type="button"
                onClick={handleFinishCapture}
                className="absolute right-4 px-5 py-2.5 bg-brand-600 text-white rounded-full font-medium text-sm active:bg-brand-700"
              >
                완료 ({capturedPhotos.length})
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          {/* 선택 모드 - 상단 바 */}
          <div className="flex items-center justify-between px-4 py-3 bg-ink-900">
            <button
              type="button"
              onClick={handleBackToCapture}
              className="text-white flex items-center gap-1 p-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm">촬영</span>
            </button>
            <h2 className="text-white font-medium">
              사진 선택 ({capturedPhotos.length}장)
            </h2>
            <div className="w-16" />
          </div>

          {/* 사진 그리드 */}
          <div className="flex-1 overflow-y-auto bg-ink-900 p-3">
            <div className="grid grid-cols-3 gap-2">
              {capturedPhotos.map((photo, idx) => {
                const isSelected = selectedIndexes.has(idx);
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleToggleSelect(idx)}
                    className={`relative aspect-square rounded-card-sm overflow-hidden border-2 transition-colors ${
                      isSelected ? 'border-brand-500' : 'border-transparent'
                    }`}
                  >
                    <img
                      src={photo.previewUrl}
                      alt={`사진 ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {/* 체크 표시 */}
                    <div
                      className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        isSelected
                          ? 'bg-brand-500 text-white'
                          : 'bg-black/40 text-white border border-white/60'
                      }`}
                    >
                      {isSelected ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <span>{idx + 1}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 하단 액션 */}
          <div className="flex items-center justify-between px-4 py-3 bg-ink-900 border-t border-ink-700" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
            <button
              type="button"
              onClick={handleToggleAll}
              className="text-white text-sm px-3 py-2 rounded-control active:bg-ink-700"
            >
              {selectedIndexes.size === capturedPhotos.length ? '전체 해제' : '전체 선택'}
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedCount === 0}
              className="px-6 py-2.5 bg-brand-600 text-white rounded-full font-medium text-sm active:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              추가 ({selectedCount}장)
            </button>
          </div>
        </>
      )}
    </div>
  );
}
